import {
  ArrowRight,
  Building2,
  FileText,
  IdCard,
  Lock,
  type LucideIcon,
  ScanFace,
  ShieldCheck,
} from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";

import type { VerificationPurpose } from "../types/verification-api.types";

export interface StepIntroCardProps {
  purpose: VerificationPurpose;
  onStart: () => void;
}

interface IntroNeed {
  icon: LucideIcon;
  label: string;
}

/** Intro / "what you'll need" — first screen of both flows. */
export function StepIntroCard({ purpose, onStart }: StepIntroCardProps) {
  const { t } = useTranslations("verification");
  const isKyc = purpose === "KYC";

  const needs: IntroNeed[] = isKyc
    ? [
        { icon: IdCard, label: t("kyc_intro_need_document") },
        { icon: ScanFace, label: t("kyc_intro_need_selfie") },
      ]
    : [
        { icon: Building2, label: t("kyb_intro_need_company") },
        { icon: FileText, label: t("kyb_intro_need_documents") },
      ];

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <span className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ShieldCheck className="size-6" aria-hidden="true" />
        </span>
        <h1 className="text-2xl font-semibold text-foreground">
          {t(isKyc ? "kyc_intro_title" : "kyb_intro_title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(isKyc ? "kyc_intro_subtitle" : "kyb_intro_subtitle")}
        </p>
      </div>

      <div>
        <p className="mb-3 text-sm font-medium text-foreground">
          {t(isKyc ? "kyc_intro_needs_title" : "kyb_intro_needs_title")}
        </p>
        <ul className="space-y-2.5">
          {needs.map((need) => {
            const NeedIcon = need.icon;
            return (
              <li key={need.label} className="flex items-center gap-3 text-sm text-foreground">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <NeedIcon className="size-4" aria-hidden="true" />
                </span>
                {need.label}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-muted p-3 text-xs text-muted-foreground">
        <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>{t(isKyc ? "kyc_intro_security" : "kyb_intro_save_note")}</span>
      </div>

      <ColocaButton variant="primary" className="w-full" iconTrailing={ArrowRight} onClick={onStart}>
        {t(isKyc ? "kyc_intro_cta" : "kyb_intro_cta")}
      </ColocaButton>
    </div>
  );
}
